import { useTranslate } from "@refinedev/core";
import { Input, TextArea } from "@libertify/ui";
import { EditableItemCard } from "../EditableItemCard";

const KEY = "experienceEdit.keyPointsCard";

export type KeyPointItemProps = {
  /** Zero-based position, shown to the reader as "POINT {index + 1}". */
  index: number;
  title: string;
  body: string;
  /** The point has content but won't be saved until it gets a title. */
  needsTitle: boolean;
  onTitleChange: (title: string) => void;
  onBodyChange: (body: string) => void;
  onRemove: () => void;
};

/**
 * One editable key point: its position, a remove action, and the title and
 * body the reader sees in the experience.
 *
 * Removal is confirmed whenever the point holds anything, because the card
 * autosaves — unlike the create flow, there is no draft to abandon.
 */
export const KeyPointItem = ({
  index,
  title,
  body,
  needsTitle,
  onTitleChange,
  onBodyChange,
  onRemove,
}: KeyPointItemProps) => {
  const t = useTranslate();

  return (
    <EditableItemCard
      label={t(`${KEY}.pointNumber`, { number: index + 1 })}
      removeLabel={t(`${KEY}.remove`)}
      removeConfirmLabel={t(`${KEY}.removeConfirm`)}
      confirmRemove={Boolean(title.trim() || body.trim())}
      onRemove={onRemove}
    >
      <Input
        value={title}
        placeholder={t(`${KEY}.titlePlaceholder`)}
        status={needsTitle ? "warning" : "default"}
        helpText={needsTitle ? t(`${KEY}.titleRequired`) : undefined}
        aria-label={t(`${KEY}.titleLabel`, { number: index + 1 })}
        onChange={(event) => onTitleChange(event.target.value)}
      />
      <TextArea
        value={body}
        placeholder={t(`${KEY}.bodyPlaceholder`)}
        aria-label={t(`${KEY}.bodyLabel`, { number: index + 1 })}
        autoSize={{ minRows: 3, maxRows: 14 }}
        onChange={(event) => onBodyChange(event.target.value)}
      />
    </EditableItemCard>
  );
};

export default KeyPointItem;
